import React, { useState } from 'react';
import { Button, Space, ColorPicker, Popover } from 'antd';
import {
  BorderOutlined,
  ArrowRightOutlined,
  ClearOutlined,
  FontSizeOutlined,
  AimOutlined,
  DeleteOutlined,
  BgColorsOutlined,
  UndoOutlined,
  RedoOutlined,
  CheckOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import styles from './DrawingToolbox.module.scss';
import { useLanguage } from '../../contexts/LanguageContext';
import FreePenIcon from '../FreePenIcon';
import EllipseIcon from '../EllipseIcon';

export type DrawingTool = 'line' | 'rect' | 'arrow' | 'circle' | 'text' | 'select' | 'image';

interface CompactDrawingToolboxProps {
  tool: DrawingTool;
  color: string;
  onToolChange: (tool: DrawingTool) => void;
  onColorChange: (color: string) => void;
  onClear: () => void;
  onSave: () => void;
  onDeleteSelected?: () => void;
  onChangeSelectedColor?: (color: string) => void;
  onUndo?: () => void;
  onRedo?: () => void;
  hasSelectedShape?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  selectedShapeColor?: string;
  onClose?: () => void;
  onOpenChange?: (isOpen: boolean) => void;
}

const CompactDrawingToolbox: React.FC<CompactDrawingToolboxProps> = ({
  tool,
  color,
  onToolChange,
  onColorChange,
  onClear,
  onSave,
  onDeleteSelected,
  onChangeSelectedColor,
  onUndo,
  onRedo,
  hasSelectedShape = false,
  canUndo = false,
  canRedo = false,
  selectedShapeColor,
  onClose,
  onOpenChange,
}) => {
  const { t } = useLanguage();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // onClose prop'u çağrıldığında popover'ı kapat
  React.useEffect(() => {
    if (onClose) {
      setIsPopoverOpen(false);
      onClose();
    }
  }, [onClose]);

  // isPopoverOpen değiştiğinde onOpenChange callback'ini çağır
  React.useEffect(() => {
    onOpenChange?.(isPopoverOpen);
  }, [isPopoverOpen, onOpenChange]);

  const toolboxContent = (
    <div 
      className={styles.compactToolbox}
      onMouseDown={(e) => {
        // Popover içindeki tıklamaları engelleme
        e.stopPropagation();
      }}
    >
      <div className={styles.toolboxContent}>
        <div className={styles.toolGroup}>
          <Space.Compact>
            <Button
              style={{ width: 28, height: 28 }}
              icon={<AimOutlined />}
              onClick={() => onToolChange('select')}
              type={tool === 'select' ? 'primary' : 'default'}
              title={t('Select')}
            />
            <Button
              style={{ width: 28, height: 28 }}
              icon={<FreePenIcon width={14} height={14} fill="#fff" />}
              onClick={() => onToolChange('line')}
              type={tool === 'line' ? 'primary' : 'default'}
              title={t('Line')}
            />
            <Button
              style={{ width: 28, height: 28 }}
              icon={<EllipseIcon width={14} height={14} fill="none" stroke="#fff" />}
              onClick={() => onToolChange('circle')}
              type={tool === 'circle' ? 'primary' : 'default'}
              title={t('Circle')}
            />
            <Button
              style={{ width: 28, height: 28 }}
              icon={<BorderOutlined />}
              onClick={() => onToolChange('rect')}
              type={tool === 'rect' ? 'primary' : 'default'}
              title={t('Rectangle')}
            />
            <Button
              style={{ width: 28, height: 28 }}
              icon={<ArrowRightOutlined />}
              onClick={() => onToolChange('arrow')}
              type={tool === 'arrow' ? 'primary' : 'default'}
              title={t('Arrow')}
            />
            <Button
              style={{ width: 28, height: 28 }}
              icon={<FontSizeOutlined />}
              onClick={() => onToolChange('text')}
              type={tool === 'text' ? 'primary' : 'default'}
              title={t('Text')}
            />
            
            <Button
              style={{ width: 28, height: 28 }}
              icon={<PictureOutlined />}
              onClick={() => onToolChange('image')}
              type={tool === 'image' ? 'primary' : 'default'}
              title={t('Image')}
            />
          </Space.Compact>
        </div>

        <div className={styles.toolGroup}>
          <ColorPicker 
            size="small" 
            value={color} 
            onChange={(c) => onColorChange(c.toHexString())}
          />
        </div>

        <Space>
          {onUndo && (
            <Button 
              style={{ width: 28, height: 28 }}
              icon={<UndoOutlined />} 
              onClick={onUndo}
              disabled={!canUndo}
              title={t('Undo')}
              className={styles.undoRedoBtn}
            />
          )}
          {onRedo && (
            <Button 
              style={{ width: 28, height: 28 }}
              icon={<RedoOutlined />} 
              onClick={onRedo}
              disabled={!canRedo}
              title={t('Redo')}
              className={styles.undoRedoBtn}
            />
          )}
          {tool === 'select' && hasSelectedShape && (
            <>
              {onChangeSelectedColor && (
                <Popover
                  content={
                    <ColorPicker 
                      size="small"
                      value={selectedShapeColor || color}
                      onChange={(c) => onChangeSelectedColor(c.toHexString())}
                    />
                  }
                  trigger="click"
                  title={t('Change Color')}
                >
                  <Button 
                    style={{ width: 28, height: 28 }}
                    icon={<BgColorsOutlined />} 
                    title={t('Change Color')}
                    className={styles.changeColorBtn}
                  />
                </Popover>
              )}
              {onDeleteSelected && (
                <Button 
                  style={{ width: 28, height: 28 }}
                  icon={<DeleteOutlined />} 
                  danger 
                  onClick={onDeleteSelected}
                  title={t('Delete Selected')}
                  className={styles.deleteBtn}
                />
              )}
            </>
          )}
                  <Button 
          icon={<ClearOutlined />} 
          onClick={onClear}
          title={t('Clear')}
          style={{ width: 28, height: 28 }}
        />
        <Button 
          icon={<CheckOutlined />} 
          onClick={() => setIsPopoverOpen(false)}
          title={t('Done')}
          style={{ 
            width: 28, 
            height: 28,
            background: 'var(--app-accent-secondary)',
            border: 'none',
            color: '#fff'
          }}
        />
        </Space>
      </div>
    </div>
  );

  return (
    <Popover
      content={toolboxContent}
      trigger="click"
      open={isPopoverOpen}
      onOpenChange={(open) => {
        // Sadece açma işlemi için set et, kapatma için set etme
        if (open) {
          setIsPopoverOpen(true);
        }
      }}
      placement="top"
      overlayClassName={styles.compactToolboxPopover}
      destroyTooltipOnHide={false}
      autoAdjustOverflow={true}
      getPopupContainer={() => document.body}
    >
      <Button
        style={{ 
          width: 14, 
          height: 14,
          minHeight: 14,
          maxHeight: 14,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: 'none',
          color: '#fff',
          padding: 0,
          lineHeight: 1
        }}
        icon={<FreePenIcon width={14} height={14} fill="#fff" />}
        title={t('Drawing Tools')}
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
      />
    </Popover>
  );
};

export default CompactDrawingToolbox; 